import {useState, useCallback, useEffect, useRef} from 'react'
import {getCurrentLanguage} from '@/i18n'
import {useTranslation} from 'react-i18next'
import {reportJavaScriptError} from '@/api/error-reporter'
import {translateErrorCode} from '@/lib/error-utils'
import type {ErrorEntry} from '@/types/error'

interface FieldValidationResult {
    fieldErrors: Record<string, string>
    isValidating: boolean
    validate: () => void
    clearErrors: () => void
}

const GENERAL_KEY = '_general'

export function useFieldValidation(
    url: string,
    body: Record<string, unknown>,
    debounceMs = 500,
): FieldValidationResult {
    const {t} = useTranslation()
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [isValidating, setIsValidating] = useState(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const bodyRef = useRef(body)
    bodyRef.current = body

    const validate = useCallback(() => {
        if (!url) return

        if (timerRef.current) {
            clearTimeout(timerRef.current)
        }

        timerRef.current = setTimeout(async () => {
            setIsValidating(true)
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': getCurrentLanguage(),
                    },
                    body: JSON.stringify(bodyRef.current),
                })

                if (response.ok) {
                    setFieldErrors({})
                } else if (response.status === 400) {
                    const data = await response.json()
                    const errors: Record<string, string> = {}
                    for (const entry of (data.errors ?? []) as ErrorEntry[]) {
                        const pointer = entry.source?.pointer
                        const key = pointer ? pointer.replace(/^\//, '') : GENERAL_KEY
                        errors[key] = translateErrorCode(entry, t)
                    }
                    setFieldErrors(errors)
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err)
                reportJavaScriptError(message, url, err instanceof Error ? err.stack : undefined)
            } finally {
                setIsValidating(false)
            }
        }, debounceMs)
    }, [url, debounceMs, t])

    const clearErrors = useCallback(() => {
        setFieldErrors({})
    }, [])

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [])

    return {fieldErrors, isValidating, validate, clearErrors}
}
